import {
  Box,
  Button,
  DateRangePicker,
  DateRangePickerValue,
  Divider,
  DropdownButton,
  DropdownButtonProps,
  FlexBox,
  Icon,
  PresetRange,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import type {
  PeriodControl,
  PeriodControlPresetValue,
  PeriodControlRangeValue,
  PeriodControlValue,
  SettingsControl,
} from 'lib-analytics-types';
import { Schedule } from 'lib-shared-types-api';
import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useDebounce, useMap, useUpdateEffect } from 'react-use';

import { analyticsApi } from 'apis/analytics';
import { NullableValues } from 'utils/typeUtils';

import { AnalyticsBusinessHours } from './components/businessHours/analyticsBusinessHours';
import { useHasAnalyticsBusinessHours } from './components/businessHours/utils/useHasAnalyticsBusinessHours';
import { formatAnalyticsPeriod } from './utils';

type DraftPeriod = {
  field: 'created_at';
  values: PeriodControlPresetValue | NullableValues<PeriodControlRangeValue>;
};

type DraftState = {
  period?: DraftPeriod;
  settings?: SettingsControl;
};

type AnalyticsPeriodProps = {
  /** The currently selected period. */
  period?: PeriodControl;
  /** The currently selected settings. */
  settings?: SettingsControl;
  /** The placeholder element to render in the button when nothing is selected. */
  placeholder?: ReactNode;
  /** The props to pass down to the Button component. */
  buttonProps?: DropdownButtonProps['buttonProps'];
  /** Callback for when the period is changed. */
  onChangePeriod: (period: PeriodControl) => void;
  /**
   * Callback for when the settings are changed. Timezone setting changes may
   * result in an adjusted period, in which case the new period is also passed.
   */
  onChangeSettings: (settings: SettingsControl, period?: PeriodControl) => void;
  /** Callback for when the period and settings are cleared. */
  onClear?: () => void;
};

/**
 * Type guard that checks whether a draft period is valid and can be propagated
 * to the main state.
 */
const isValidDraft = (draft: DraftPeriod): draft is PeriodControl => {
  return (draft as PeriodControl).values.every((item) => item !== null);
};

/** Type guard that checks whether a draft period is a preset. */
const isPreset = (
  values: DraftPeriod['values'],
): values is PeriodControlPresetValue => {
  return (values as PeriodControlPresetValue).length === 1;
};

export const AnalyticsPeriod = ({
  period,
  settings,
  placeholder = 'Select period',
  buttonProps,
  onChangePeriod,
  onChangeSettings,
  onClear,
}: AnalyticsPeriodProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const timezone = settings?.timezone ?? moment.tz.guess();

  const [draft, { set: patchDraft, setAll: setDraft }] = useMap<DraftState>({
    period,
    settings: {
      timezone,
      ...settings,
    },
  });

  const { presets, isLoading } = analyticsApi.useReadPeriodPresetsQuery(
    { timezone },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        presets: data?.data.presets ?? [],
      }),
    },
  );

  const hasBusinessHours = useHasAnalyticsBusinessHours();

  // Updates the draft period when the main state changes.
  useUpdateEffect(() => {
    const shouldUpdate =
      !isEqual(draft.period, period) || !isEqual(draft.settings, settings);

    if (shouldUpdate) {
      setDraft({ period, settings });
    }
  }, [period, settings]);

  // Updates the period only if the draft is different and is a valid date range. If the draft is a
  // preset, the period is updated immediately, otherwise the period is updated after a delay.
  useDebounce(
    () => {
      if (!draft.period || !draft.settings || !isValidDraft(draft.period))
        return;

      const periodShouldUpdate = !isEqual(period, draft.period);
      const settingsShouldUpdate = !isEqual(settings, draft.settings);

      // Updating the timezone setting may result in an adjusted period when working with custom
      // date ranges, so we update both at the same time to avoid inconsistencies.
      if (settingsShouldUpdate && periodShouldUpdate) {
        onChangeSettings(draft.settings, draft.period);
        return;
      }

      if (periodShouldUpdate) onChangePeriod(draft.period);
      if (settingsShouldUpdate) onChangeSettings(draft.settings);
    },
    draft.period && isPreset(draft.period.values) ? 0 : 1000,
    [draft],
  );

  /** The date range value passed down to the datepicker component. */
  const value = useMemo((): DateRangePickerValue => {
    let startDate: string | null = null;
    let endDate: string | null = null;

    if (draft.period) {
      if (isPreset(draft.period.values)) {
        const [preset] = draft.period.values;
        const presetValue = preset.value;
        const presetData = presets.find(({ value }) => value === presetValue);

        startDate = presetData?.start ?? null;
        endDate = presetData?.end ?? null;
      } else {
        const [start, end] = draft.period.values;

        startDate = start?.value ?? null;
        endDate = end?.value ?? null;
      }
    }

    return {
      startDate: startDate ? moment(startDate).tz(timezone) : null,
      endDate: endDate ? moment(endDate).tz(timezone) : null,
    };
  }, [draft, presets, timezone]);

  /** Updates the draft period with the new date range value. */
  const handleChange = ({
    presetKey,
    ...dateRangePickerValue
  }: DateRangePickerValue) => {
    if (presetKey) {
      setDraft({
        period: {
          field: 'created_at',
          values: [
            {
              value: presetKey ?? '',
            },
          ],
        },
        settings: {
          ...settings,
          timezone: dateRangePickerValue.timezone ?? moment.tz.guess(),
        },
      });
    } else {
      const startDate = dateRangePickerValue.startDate?.clone().startOf('day');
      const endDate = dateRangePickerValue.endDate?.clone().endOf('day');

      const startDateValue: PeriodControlValue | null = startDate
        ? {
            value: startDate.toISOString(),
          }
        : null;
      const endDateValue: PeriodControlValue | null = endDate
        ? {
            value: endDate.toISOString(),
          }
        : null;

      setDraft({
        period: { field: 'created_at', values: [startDateValue, endDateValue] },
        settings: {
          ...settings,
          timezone: dateRangePickerValue.timezone ?? moment.tz.guess(),
        },
      });
    }
  };

  /** Updates the draft settings when a schedule is selected. */
  const handleChangeSchedule = useCallback(
    (schedule?: Schedule) => {
      patchDraft('settings', {
        ...settings,
        timezone: schedule?.timezone ?? moment.tz.guess(),
        schedule_id: schedule?.id,
      });
    },
    [patchDraft, settings],
  );

  /**
   * The text displayed in the button, derived from the original period, not the
   * draft period.
   */
  const buttonText = useMemo(
    (): ReactNode =>
      period ? formatAnalyticsPeriod(period, timezone, presets) : placeholder,
    [placeholder, period, presets, timezone],
  );

  const dateRangeMaxDate = moment().tz(timezone).add(1, 'day');

  const dateRangePresets: PresetRange[] | undefined = useMemo(() => {
    return presets.map(({ value, label, start, end }) => ({
      label,
      key: value,
      startDate: moment.tz(start, timezone),
      endDate: moment.tz(end, timezone),
    }));
  }, [presets, timezone]);

  const hasSchedule = !!settings?.schedule_id;

  return (
    <DropdownButton
      icon="calendar"
      buttonProps={{
        variant: 'secondary',
        isLoading,
        label: timezone !== moment.tz.guess() ? timezone : undefined,
        styleText: false,
        ...buttonProps,
      }}
      onToggle={setIsOpen}
      dropdown={
        <FlexBox flexDirection="column" width="min-content">
          {isOpen && (
            <DateRangePicker
              startDate={value.startDate}
              endDate={value.endDate}
              maxDate={dateRangeMaxDate}
              onChange={handleChange}
              inputsPlacement="top"
              presets={dateRangePresets}
              presetKey={
                period && isPreset(period.values)
                  ? period.values[0].value
                  : undefined
              }
              timezone={timezone}
              withTimezoneSelector
              timezoneSelectorProps={{
                disabled: hasSchedule,
                tooltipProps: hasSchedule
                  ? {
                      content:
                        'The timezone of the selected business hours schedule is applied.',
                      disabled: false,
                    }
                  : undefined,
              }}
            />
          )}

          {hasBusinessHours && (
            <>
              <Divider />

              <Box padding="300" flex={1}>
                <AnalyticsBusinessHours
                  scheduleId={settings?.schedule_id}
                  isPeriodSelected={!!period}
                  onChangeSchedule={handleChangeSchedule}
                />
              </Box>
            </>
          )}

          {onClear && (
            <>
              <Divider />

              <FlexBox padding="300" flex={1} justifyContent="flex-end">
                <Button
                  variant="secondary"
                  disabled={!period}
                  onClick={onClear}
                >
                  Clear
                </Button>
              </FlexBox>
            </>
          )}
        </FlexBox>
      }
      fixed
    >
      <FlexBox alignItems="center" gap="200">
        <Text.Paragraph
          variant="3"
          wrap="nowrap"
          color={placeholder && !period ? typography.color.tertiary : 'inherit'}
        >
          {buttonText}
        </Text.Paragraph>

        {settings?.schedule_id && (
          <Tooltip
            content="Business hours schedule applied"
            wrap="div"
            wrapProps={{ style: { display: 'flex' } }}
          >
            <Icon name="clock-three" />
          </Tooltip>
        )}
      </FlexBox>
    </DropdownButton>
  );
};
